export function applyContrastAdjustment(
  image: HTMLImageElement,
  minPercent: number,
  maxPercent: number
): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  
  if (!ctx) {
    throw new Error('Could not get canvas context');
  }

  // Set canvas dimensions to match image
  canvas.width = image.width;
  canvas.height = image.height;

  // Draw the original image
  ctx.drawImage(image, 0, 0);

  // Get image data for pixel manipulation
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;

  // Convert percentages to 0-255 range
  const minValue = (minPercent / 100) * 255;
  const maxValue = (maxPercent / 100) * 255;

  // Apply contrast adjustment to each pixel
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];     // Red
    const g = data[i + 1]; // Green
    const b = data[i + 2]; // Blue
    
    // Apply contrast adjustment to each channel independently
    let adjustedR = r;
    let adjustedG = g;
    let adjustedB = b;
    
    if (maxValue > minValue) {
      // Normalize each channel to 0-1 range based on min/max
      const normalizedR = Math.max(0, Math.min(1, (r - minValue) / (maxValue - minValue)));
      const normalizedG = Math.max(0, Math.min(1, (g - minValue) / (maxValue - minValue)));
      const normalizedB = Math.max(0, Math.min(1, (b - minValue) / (maxValue - minValue)));
      
      adjustedR = normalizedR * 255;
      adjustedG = normalizedG * 255;
      adjustedB = normalizedB * 255;
    }

    // Apply adjusted values to each channel
    data[i] = adjustedR;     // Red
    data[i + 1] = adjustedG; // Green
    data[i + 2] = adjustedB; // Blue
    // Alpha channel (data[i + 3]) remains unchanged
  }

  // Put the modified image data back to canvas
  ctx.putImageData(imageData, 0, 0);

  return canvas;
}

export function createAdjustedImageElement(
  image: HTMLImageElement,
  minPercent: number,
  maxPercent: number
): HTMLImageElement {
  const canvas = applyContrastAdjustment(image, minPercent, maxPercent);
  
  // Convert canvas to image element
  const adjustedImage = new Image();
  adjustedImage.src = canvas.toDataURL();
  
  return adjustedImage;
} 